import React, { useEffect, useState } from "react";
import { TrendStrip } from "../components/trend/TrendStrip";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail, Users, Shirt, Image as ImageIcon, Calendar, Wallet,
  PlayCircle, Sparkles, Heart, ArrowRight,
  Gamepad2, Megaphone, MessageCircle, Trophy,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CountdownCard from "../components/CountdownCard";
import FeaturedCoupleSection from "../components/FeaturedCoupleSection";
import { ShowcaseFilms } from "../components/showcase/ShowcaseFilms";
import HeroSlider from "../components/home/HeroSlider";
import FeaturedPlanners from "../components/home/FeaturedPlanners";
import TestimonialReel from "../components/home/TestimonialReel";
import AudienceSwitcher from "../components/home/AudienceSwitcher";
import MilestoneTrack from "../components/home/MilestoneTrack";
import { FeatureWeddingPromo, TestimonialsSection } from "../components/home/FeatureWedding";
import { SocialPromo } from "../components/home/SocialPromo";
import Seo, { buildFaqSchema } from "../components/Seo";
import { api } from "../lib/api";
import { SponsoredVendors } from "../components/social/SponsoredVendors";

// Showcase card always counts down ~4 months ahead so it never reads 00.
const DEMO_WEDDING_DATE = new Date(Date.now() - 12 * 86400000).toISOString().slice(0, 10);

const PODCAST_IMG = "https://images.unsplash.com/photo-1712932115370-4f9d354ced6b?w=1400";
const COUPLE_DASH_IMG = "https://images.unsplash.com/photo-1718997534125-052b2ff48cd0?w=1200";
const COUPLE_DASH_PHONE = "https://images.unsplash.com/photo-1525258801032-ed95063bf83e?w=600";
const OFFICIAL_LOGO = `${process.env.REACT_APP_BACKEND_URL}/api/brand/logo.png`;

const DEMO_SITE = "/site/emmanuel-and-jennifer";

const FEATURES = [
  { icon: Mail, title: "Digital Invites & RSVP", desc: "Beautiful invites, RSVP tracking, meal choices and more.", to: "/demo/rsvp" },
  { icon: Users, title: "Guest List Manager", desc: "Organize guests, plus-ones, seating, notes and more.", to: "/demo/guests" },
  { icon: Shirt, title: "Dress & Tux Tracker", desc: "Track sizes, fittings, colors and pickup dates all in one place.", to: "/demo/dress-tux" },
  { icon: ImageIcon, title: "Memory Vault", desc: "Collect, organize and download your precious memories.", to: "/demo/memories" },
  { icon: Calendar, title: "Planning & Timeline", desc: "Stay on track with checklists, tasks and important dates.", to: "/demo/timeline" },
  { icon: Wallet, title: "Budget Tracker", desc: "Manage vendors, payments, deposits and balances.", to: "/demo/budget" },
];

const GAMES = [
  {
    icon: Gamepad2,
    badge: "Live Game",
    title: "Locked In Questions",
    desc: "Curated trivia your guests vote on live. Real-time leaderboard, cinematic reveals.",
    cta: "Play the demo",
    to: "/play/DEMO26",
    accent: true,
  },
  {
    icon: Heart,
    badge: "Quiz",
    title: "Compatibility Test",
    desc: "Discover your love archetype together. 40 questions, one shareable card.",
    cta: "Take the test",
    to: "/signup",
  },
  {
    icon: Megaphone,
    badge: "Buzz",
    title: "Get Locked In Announcements",
    desc: "Featured weddings, podcast drops, and community moments. Bold breaking-news energy.",
    cta: "Read the buzz",
    to: "/announcements",
  },
];

const DASH_NAV = [
  { label: "Dashboard", to: "/dashboard" },
  { label: "Website", to: "/dashboard/website" },
  { label: "Guests", to: "/dashboard/guests" },
  { label: "RSVPs", to: "/dashboard/rsvps" },
  { label: "Events", to: "/dashboard/events" },
  { label: "Dress & Tux", to: "/dashboard/dress-tux" },
  { label: "Timeline", to: "/dashboard/timeline" },
  { label: "Budget", to: "/dashboard/budget" },
  { label: "Registry", to: "/dashboard/registry" },
  { label: "Memories", to: "/dashboard/memories" },
  { label: "Settings", to: "/dashboard/settings" },
];

const TOPIC_FALLBACK = [
  { slug: "planning", label: "Planning Tips" },
  { slug: "marriage", label: "Marriage Advice" },
  { slug: "love", label: "Love & Communication" },
  { slug: "finance", label: "Finances" },
  { slug: "real", label: "Real Couple Stories" },
  { slug: "qa", label: "Q&A With Don" },
];

export default function Homepage() {
  const [episodes, setEpisodes] = useState([]);
  const [topics, setTopics] = useState([]);
  const [gameCode, setGameCode] = useState("");
  const [featuredAnnouncements, setFeaturedAnnouncements] = useState([]);
  const [theme, setTheme] = useState("noir");

  useEffect(() => {
    api.get("/podcast/episodes").then((r) => setEpisodes(r.data.slice(0, 3))).catch(() => {});
    api.get("/podcast/topics").then((r) => setTopics(r.data?.length ? r.data : TOPIC_FALLBACK)).catch(() => setTopics(TOPIC_FALLBACK));
    api.get("/announcements?featured_only=true").then((r) => setFeaturedAnnouncements((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  function joinGame(e) {
    e.preventDefault();
    const code = gameCode.trim().toUpperCase();
    if (code) window.location.href = `/play/${code}`;
  }

  return (
    <div className="bg-[var(--bg)] overflow-x-clip">
      <Seo
        rawTitle="Locked In Forever | Wedding Websites, Planning & Vendors"
        description="Create your wedding website, manage RSVPs, plan your big day, and discover wedding venues and vendors with Locked In Forever."
        path="/"
        keywords={[
          "wedding planning tool",
          "wedding milestone tracker",
          "wedding planning checklist app",
          "luxury wedding website",
          "wedding website builder",
          "online wedding rsvp",
          "couple compatibility test",
          "wedding memory vault",
          "wedding planning podcast",
          "wedding games",
          "newlywed game",
          "bridal roster",
        ]}
        schema={buildFaqSchema([
          ["Is LockedInForever free to start?", "Yes — every couple can build a beautiful wedding site, send RSVPs, and start a memory vault for free. Premium and Luxury plans unlock advanced features."],
          ["Can guests RSVP without an account?", "Absolutely. Every guest gets a one-tap RSVP link — no sign-up required."],
          ["Does LockedInForever replace my wedding planner?", "No. LockedInForever keeps your plans organized and every milestone on track. Invite your planner and you both work from the same plan."],
          ["Does LockedInForever work for planners and venues?", "Yes — planners and venues get white-labeled client portals, shared milestone tracking and a marketplace profile."],
        ])}
      />
      <Navbar overlay tone={theme === "ivory" ? "dark" : "light"} />

      <HeroSlider onTheme={setTheme} aside={<CountdownCard names="Emmanuel & Jennifer" date={DEMO_WEDDING_DATE} />} />
      <TrendStrip />
      <FeatureWeddingPromo />
      <SocialPromo />
      <MilestoneTrack />
      <AudienceSwitcher theme={theme} />
      <TestimonialsSection />

      <section className="px-6 md:px-12 pt-16 max-w-7xl mx-auto"><SponsoredVendors place="home" /></section>
      <FeaturedPlanners />
      <TestimonialReel />

      {/* FEATURES — 6 in a row */}
      <section id="features" className="section-pad">
        <div className="container-wide px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 md:gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={`feature-${i}`}
              >
                <Link to={f.to} className="group block text-center px-2">
                  <div className="mb-4 flex justify-center">
                    <f.icon className="w-9 h-9 text-[var(--gold)] transition-transform group-hover:scale-110" strokeWidth={1.2} />
                  </div>
                  <h3 className="text-[12px] uppercase tracking-[0.18em] font-semibold mb-2 text-[var(--ink)] group-hover:text-[var(--gold)] transition-colors">{f.title}</h3>
                  <p className="text-xs text-[var(--muted)] leading-relaxed">{f.desc}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GAMES & INTERACTIVE — signature feature spotlight */}
      <section id="games" className="bg-[var(--ink)] text-white py-20 md:py-28 relative overflow-hidden" data-testid="games-section">
        {/* Decorative gold gradient blobs */}
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#C8A96B]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#C8A96B]/10 blur-3xl pointer-events-none" />

        <div className="container-wide px-6 md:px-12 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-3 flex items-center gap-2">
                <Trophy className="w-3.5 h-3.5" /> The Locked In Experience
              </p>
              <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                Games your guests
                <span className="block font-script text-[var(--gold)] text-5xl md:text-6xl lg:text-7xl mt-2">won't stop talking about.</span>
              </h2>
              <p className="text-white/70 mt-6 max-w-xl">
                Trivia, compatibility, live leaderboards, and Get-Locked-In announcements. The cinematic moments your wedding deserves — built right into your dashboard.
              </p>
            </div>

            {/* Join-a-game code input */}
            <div className="lg:col-span-5 min-w-0">
              <form onSubmit={joinGame} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5" data-testid="join-game-form">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--gold)] mb-3">Got a code from a couple?</p>
                <div className="flex gap-2">
                  <input
                    value={gameCode}
                    onChange={(e) => setGameCode(e.target.value.toUpperCase())}
                    placeholder="ENTER CODE"
                    maxLength={8}
                    data-testid="game-code-input"
                    className="flex-1 min-w-0 bg-transparent border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 font-mono tracking-[0.18em] focus:border-[var(--gold)] outline-none"
                  />
                  <button type="submit" data-testid="join-game-submit" className="btn-gold whitespace-nowrap">
                    Join <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[11px] text-white/50 mt-3">Or try the demo: <Link to="/play/DEMO26" className="text-[var(--gold)] underline" data-testid="demo-game-link">code DEMO26</Link></p>
              </form>
            </div>
          </div>

          {/* Game cards */}
          <div className="grid md:grid-cols-3 gap-5">
            {GAMES.map((g, i) => (
              <motion.div
                key={g.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                data-testid={`game-card-${i}`}
              >
                <Link
                  to={g.to}
                  className={`group block h-full rounded-3xl p-7 border transition-all duration-300 ${
                    g.accent
                      ? "bg-gradient-to-br from-[#C8A96B]/20 to-transparent border-[#C8A96B]/40 hover:border-[var(--gold)]"
                      : "bg-white/5 border-white/10 hover:border-[#C8A96B]/60 hover:bg-white/[0.07]"
                  }`}
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${g.accent ? "bg-[var(--gold)] text-[var(--ink)]" : "bg-white/10 text-[var(--gold)]"}`}>
                      <g.icon className="w-5 h-5" strokeWidth={1.6} />
                    </div>
                    <span className={`text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full ${g.accent ? "bg-[var(--gold)] text-[var(--ink)]" : "bg-white/10 text-white/70"}`}>
                      {g.badge}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl mb-2 group-hover:text-[var(--gold)] transition-colors">{g.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed mb-5">{g.desc}</p>
                  <span className="inline-flex items-center gap-1 text-sm text-[var(--gold)] group-hover:gap-2 transition-all">
                    {g.cta} <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Featured announcements ticker */}
          {featuredAnnouncements.length > 0 && (
            <div className="mt-14 border-t border-white/10 pt-10" data-testid="featured-announcements">
              <div className="flex items-center justify-between mb-5">
                <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] flex items-center gap-2">
                  <Megaphone className="w-3.5 h-3.5" /> Locked In Buzz
                </p>
                <Link to="/announcements" className="text-xs text-white/60 hover:text-[var(--gold)] transition-colors">All announcements →</Link>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {featuredAnnouncements.map((a) => (
                  <Link
                    key={a.announcement_id}
                    to={a.link_url || "/announcements"}
                    className="block bg-white/[0.03] border border-white/10 rounded-2xl p-5 hover:border-[#C8A96B]/40 transition-colors"
                  >
                    <p className="text-[10px] uppercase tracking-[0.22em]" style={{ color: a.accent_color || "var(--gold)" }}>{a.is_admin_post ? "Editorial" : "Couple Drop"}</p>
                    <p className="font-serif text-lg mt-2 leading-snug">{a.title}</p>
                    <p className="text-xs text-white/50 mt-2 line-clamp-2">{a.body}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* DASHBOARD PREVIEW — sidebar mockup + couple website preview */}
      <section id="builder" className="bg-[var(--bg-warm)] py-20 md:py-24">
        <div className="container-wide px-6 md:px-12 grid lg:grid-cols-2 gap-8">
          {/* Left: Wedding Dashboard mock */}
          <Link to="/dashboard" className="block bg-white rounded-3xl p-6 md:p-8 border border-[var(--line)] shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:border-[var(--gold)] transition-colors" data-testid="dash-preview-card">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)] mb-5">Your Wedding Dashboard</p>
            <div className="grid grid-cols-12 gap-5">
              <div className="col-span-4 space-y-1.5 bg-[var(--bg-warm)] rounded-xl p-3">
                {DASH_NAV.map((n, idx) => (
                  <div key={n.label} className={`text-[12px] px-3 py-1.5 rounded-md ${idx === 0 ? "bg-white text-[var(--ink)] font-medium shadow-sm" : "text-[var(--muted)]"}`}>{n.label}</div>
                ))}
              </div>
              <div className="col-span-8 space-y-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)] mb-3">What's Next</p>
                  <div className="space-y-2.5">
                    {[
                      { t: "Choose your website template", done: true },
                      { t: "Send your save the dates", done: true },
                      { t: "Add ceremony and reception details", done: true },
                      { t: "Collect RSVP responses", val: "75%" },
                      { t: "Track dress & tux sizes", val: "3/8" },
                      { t: "Upload engagement photos", val: "0/1" },
                    ].map((it) => (
                      <div key={it.t} className="flex items-center gap-2 text-[12px]">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${it.done ? "bg-[var(--gold)] text-white" : "border border-[var(--line)]"}`}>
                          {it.done && <span className="text-[10px]">✓</span>}
                        </div>
                        <span className={`flex-1 ${it.done ? "" : "text-[var(--muted)]"}`}>{it.t}</span>
                        {it.val && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg-warm)] text-[var(--muted)]">{it.val}</span>}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)] mb-3">Recent Memories</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[COUPLE_DASH_IMG, "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=400", "https://images.unsplash.com/photo-1525258801032-ed95063bf83e?w=400", "https://images.pexels.com/photos/11296094/pexels-photo-11296094.jpeg?w=400"].map((src, i) => (
                      <div key={i} className="aspect-square rounded-lg overflow-hidden bg-[var(--bg-warm)]"><img src={src} alt="" className="w-full h-full object-cover" /></div>
                    ))}
                  </div>
                  <span className="btn-outline-gold w-full mt-4 justify-center text-[11px] inline-flex">Go to Memory Vault</span>
                </div>
              </div>
            </div>
          </Link>

          {/* Right: Couple Website preview - links to demo */}
          <Link to={DEMO_SITE} className="block bg-white rounded-3xl border border-[var(--line)] shadow-[0_8px_30px_rgba(0,0,0,0.04)] overflow-hidden relative hover:border-[var(--gold)] transition-colors" data-testid="demo-site-link">
            <div className="flex items-center justify-around py-3 border-b border-[var(--line)] text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
              {["Home", "Our Story", "Events", "RSVP", "Gallery", "Registry"].map((t, i) => (
                <span key={t} className={i === 0 ? "text-[var(--gold)] font-semibold" : ""}>{t}</span>
              ))}
            </div>
            <div className="relative h-[280px] md:h-[340px]">
              <img src={COUPLE_DASH_IMG} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/40" />
              <div className="absolute top-3 left-3 bg-[var(--gold)] text-white text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full">Demo</div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white">
                  <p className="font-script text-5xl md:text-6xl">Emmanuel & Jennifer</p>
                  <p className="text-[11px] uppercase tracking-[0.22em] mt-2">Just married · Atlanta, Georgia</p>
                </div>
              </div>
            </div>
            <div className="p-6 text-center">
              <p className="font-script text-3xl text-[var(--gold)]">Our Story</p>
              <p className="text-sm text-[var(--muted)] mt-2 max-w-md mx-auto">Two hearts, one love, a lifetime to go. From the moment we met, we knew our story was one worth sharing forever.</p>
              <span className="btn-gold mt-5 inline-flex">Visit Demo Site</span>
            </div>
            {/* Phone mockup floating */}
            <div className="hidden md:block absolute -right-2 -bottom-4 w-32 lg:w-36 rounded-3xl overflow-hidden border-4 border-white shadow-2xl rotate-3">
              <img src={COUPLE_DASH_PHONE} alt="" className="w-full h-full object-cover aspect-[9/16]" />
            </div>
          </Link>
        </div>
      </section>

      {/* PODCAST */}
      <section className="section-pad bg-[var(--bg)]">
        <div className="container-wide px-6 md:px-12 grid lg:grid-cols-2 gap-10 items-stretch">
          {/* Left: dark podcast card */}
          <div className="relative rounded-3xl overflow-hidden bg-[var(--ink)] text-white min-h-[420px] flex">
            <img src={PODCAST_IMG} alt="Mr & Mrs Don podcast" className="absolute inset-0 w-full h-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
            <div className="relative z-10 p-8 md:p-10 flex flex-col justify-end ml-auto text-right max-w-[60%]">
              <p className="font-serif text-2xl md:text-3xl leading-tight">Locked In Lessons</p>
              <p className="font-script text-[var(--gold)] text-3xl md:text-4xl my-1">with</p>
              <p className="font-serif text-3xl md:text-4xl mb-4">Mr. & Mrs. Don</p>
              <p className="text-white/80 text-sm mb-5">Real talk. Real love. Real advice.<br />For the journey ahead.</p>
              <Link to="/podcast" className="btn-gold inline-flex self-end" data-testid="podcast-listen-now">Listen Now</Link>
            </div>
          </div>

          {/* Right: Latest episodes + topics */}
          <div className="bg-white border border-[var(--line)] rounded-3xl p-7 md:p-9">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)] mb-4">Latest Episodes</p>
            <div className="space-y-4">
              {(episodes.length ? episodes : [
                { episode_id: "p1", title: 'Communication Before "I Do"', episode_number: 12, duration_min: 32, thumbnail_url: COUPLE_DASH_IMG },
                { episode_id: "p2", title: "Building an Empire Together", episode_number: 11, duration_min: 28, thumbnail_url: COUPLE_DASH_IMG },
                { episode_id: "p3", title: "Budgeting for Your Big Day", episode_number: 10, duration_min: 26, thumbnail_url: COUPLE_DASH_IMG },
              ]).map((ep) => (
                <Link key={ep.episode_id} to="/podcast" className="flex items-center gap-4 group cursor-pointer hover:bg-[var(--bg-warm)] rounded-xl p-2 -mx-2 transition" data-testid={`ep-${ep.episode_id}`}>
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-[var(--bg-warm)] shrink-0">
                    {ep.thumbnail_url ? <img src={ep.thumbnail_url} alt={ep.title} className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-base truncate">{ep.title}</p>
                    <p className="text-[11px] text-[var(--muted)]">Episode {ep.episode_number} · {ep.duration_min} min</p>
                  </div>
                  <span className="w-9 h-9 rounded-full border border-[var(--gold)] flex items-center justify-center text-[var(--gold)] group-hover:bg-[var(--gold)] group-hover:text-white transition-colors">
                    <PlayCircle className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>

            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)] mt-8 mb-3">Browse Topics</p>
            <div className="grid grid-cols-2 gap-2">
              {topics.slice(0, 6).map((t) => (
                <Link key={t.slug} to={`/podcast?topic=${encodeURIComponent(t.slug)}`} className="text-[12px] px-4 py-2 border border-[var(--line)] rounded-full text-[var(--ink-soft)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition text-center">
                  {t.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>


      <ShowcaseFilms />
      <FeaturedCoupleSection />

      {/* BLACK CTA STRIP */}
      <section className="bg-[var(--ink)] text-white">
        <div className="container-wide px-6 md:px-12 py-7 md:py-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={OFFICIAL_LOGO} alt="Locked In Forever" className="h-14 w-auto" draggable="false" />
            <div>
              <p className="font-serif text-xl md:text-2xl tracking-wide">LOCKED IN FOREVER</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/50 mt-1">Your love. Your story. Locked in forever.</p>
            </div>
          </div>
          <div className="flex flex-col items-center md:items-end gap-2">
            <Link to="/signup" className="btn-gold" data-testid="cta-start-journey">
              Start Your Journey Today <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-[11px] text-white/50">Free to start. Forever to remember.</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
